import { dimensionUnits } from "@redotech/redo-model/dimension-conversion";
import {
  inboundShipmentFileIdFormat,
  inboundShipmentIdFormat,
  inboundShipmentProductIdFormat,
  locationIdFormat,
  packingTemplateIdFormat,
  productFamilyIdFormat,
  productIdFormat,
  purchaseOrderIdFormat,
  supplierIdFormat,
  transferOrderIdFormat,
} from "@redotech/redo-model/ids";
import { PackingModeSchema } from "@redotech/redo-model/inventory/packing-mode";
import { PackingTemplateTypeSchema } from "@redotech/redo-model/inventory/packing-template";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { InboundShipmentStatus } from "./get-shipments";

const DimensionUnitSchema = createEnum(dimensionUnits);

const DimensionSchema_ = z.object({
  value: z.number(),
  unit: DimensionUnitSchema,
});

export const DimensionSchema: z.ZodType<Dimension> = DimensionSchema_;
export interface Dimension extends z.infer<typeof DimensionSchema_> {}

const DimensionsSchema_ = z.object({
  length: DimensionSchema.nullish(),
  width: DimensionSchema.nullish(),
  height: DimensionSchema.nullish(),
});

export const DimensionsSchema: z.ZodType<Dimensions> = DimensionsSchema_;
export interface Dimensions extends z.infer<typeof DimensionsSchema_> {}

const PackingTemplateOptionSchema_ = z.object({
  id: packingTemplateIdFormat.schema,
  name: z.string(),
  templateType: PackingTemplateTypeSchema,
  dimensions: DimensionsSchema.nullish(),
  unitsPerBox: z.number().nullish(),
});

export const PackingTemplateOptionSchema: z.ZodType<PackingTemplateOption> =
  PackingTemplateOptionSchema_;
export interface PackingTemplateOption
  extends z.infer<typeof PackingTemplateOptionSchema_> {}

const ShipmentProductSchema_ = z.object({
  id: inboundShipmentProductIdFormat.schema,
  productId: productIdFormat.schema,
  purchaseOrderId: purchaseOrderIdFormat.schema.nullish(),
  transferOrderId: transferOrderIdFormat.schema.nullish(),
  title: z.string(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  barcodes: z.array(z.string()),
  image: z.string().nullish(),
  productFamilyId: productFamilyIdFormat.schema.nullish(),
  productFamilyTitle: z.string().nullish(),
  quantity: z.number().int(),
  receivedQuantity: z.number().int(),
  discrepancy: z.number().int(),
  damagedQuantity: z.number().int(),
  missingQuantity: z.number().int(),
  packingMode: PackingModeSchema.nullish(),
  packingTemplateId: packingTemplateIdFormat.schema.nullish(),
  productDimensions: DimensionsSchema.nullish(),
  unitCost: z.number().nullish(),
  costCurrency: z.string().nullish(),
  retailPrice: z.number().nullish(),
  packingTemplates: z.array(PackingTemplateOptionSchema),
});

export const ShipmentProductSchema: z.ZodType<ShipmentProduct> =
  ShipmentProductSchema_;
export interface ShipmentProduct
  extends z.infer<typeof ShipmentProductSchema_> {}

// Inbound shipment file
export const InboundShipmentFileSchema_ = z.object({
  id: inboundShipmentFileIdFormat.schema,
  title: z.string(),
  url: z.string(),
  contentType: z.string(),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  createdAt: zExt.instant(),
});

export const InboundShipmentFileSchema: z.ZodType<InboundShipmentFile> =
  InboundShipmentFileSchema_;
export interface InboundShipmentFile
  extends z.infer<typeof InboundShipmentFileSchema_> {}

const ShipmentDetailsSchema_ = z.object({
  id: inboundShipmentIdFormat.schema,
  shipmentId: z.string().nullish(),
  transferOrders: z.array(
    z
      .object({ id: transferOrderIdFormat.schema, transferNumber: z.string() })
      .nullish(),
  ),
  purchaseOrders: z.array(
    z.object({ id: purchaseOrderIdFormat.schema, poNumber: z.string() }),
  ),
  status: createEnum([
    InboundShipmentStatus.PENDING,
    InboundShipmentStatus.IN_TRANSIT,
    InboundShipmentStatus.PARTIALLY_RECEIVED,
    InboundShipmentStatus.RECEIVED,
  ]),
  location: z
    .object({ id: locationIdFormat.schema, name: z.string() })
    .nullish(),
  supplier: z
    .object({
      id: supplierIdFormat.schema,
      name: z.string(),
      leadTimeDays: z.number().int().nullish(),
    })
    .nullish(),
  carrier: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  estimatedArrivalDate: zExt.plainDate().nullish(),
  actualArrivalDate: zExt.plainDate().nullish(),
  freightType: z.string().nullish(),
  paymentTerms: z.string().nullish(),
  freightCost: z.number(),
  freightCurrency: z.string(),
  dutyCost: z.number(),
  dutyCurrency: z.string(),
  customVolumeCubicMeters: z.number().nullish(),
  products: z.array(ShipmentProductSchema),
  files: z.array(InboundShipmentFileSchema),
  skuCount: z.number().int(),
  itemCount: z.number().int(),
  receivedCount: z.number().int(),
  damagedCount: z.number().int(),
  missingCount: z.number().int(),
  createdAt: zExt.instant(),
  updatedAt: zExt.instant(),
});

export const ShipmentDetailsSchema: z.ZodType<ShipmentDetails> =
  ShipmentDetailsSchema_;
export interface ShipmentDetails
  extends z.infer<typeof ShipmentDetailsSchema_> {}

export const getShipment = {
  name: "getShipment" as const,
  input: z.object({ shipmentId: inboundShipmentIdFormat.schema }),
  output: z.object({ shipment: ShipmentDetailsSchema }),
};
